<script setup lang="ts" generic="T extends object = Record<string, number>">
import type { ChartContext } from '#ui-kit/charts/lib/context'
import { formatValue } from '@forgd/formatter'
import { VisCrosshair, VisTooltip } from '@unovis/vue'
import { computed, getCurrentInstance, inject, useAttrs } from 'vue'
import { chartContextKey } from '#ui-kit/charts/lib/context'
import { renderTooltip, tooltipAttributes } from '#ui-kit/charts/lib/render-tooltip'

const props = withDefaults(defineProps<ChartOverlayProps>(), {
  xAxis: 'month',
})

const emit = defineEmits<ChartOverlayEmitProps<T>>()

// slots
const slots = defineSlots<{
  default: (attrs: Omit<ChartOverlayData<T>, 'x'> & { x: number }) => void
}>()

const defaultSlot = slots.default
const attrs = useAttrs()
const instance = getCurrentInstance()

// inject store (for meta injection, and to update hovered values)
const store = inject(chartContextKey, undefined)

// meta
const meta = computed<ChartContext['meta'] | undefined>(() => {
  return props.meta ? props.meta : store ? store.meta : undefined
})

// cache unovis tooltip template values
let prevData: T
let prevTemplate = ''

// tooltip
const container = document.createElement('div')
const tooltip = document.createElement('div')
tooltip.className = ` py-2 px-2.5 rounded
                      flex-col justify-start items-start gap-2 inline-flex
                      bg-primary-900 text-white
                      text-xs leading-normal
                      shadow-lg`
container.appendChild(tooltip)

// computed properties
const crosshairOptions = computed(() => {
  // input
  const { meta: _, color, ...rest } = props

  // variables
  const xAxis = String((meta.value ? meta.value.xAxis : undefined) || props.xAxis)
  const xLabel = (meta.value ? meta.value.labels?.x : undefined) || formatValue(xAxis, 'string:title')

  // template
  const template = (d: T, xValue: number) => {
    const record = d as Record<string, unknown>

    // determine index (use xAxis if available, otherwise use xValue)
    const indexValue = record[xAxis] ?? xValue
    const numericIndex = Number(indexValue ?? 0)
    const index = Number.isFinite(numericIndex) ? numericIndex : 0

    // re-render only if the data changes
    if (d !== prevData) {
      // update cached reference
      prevData = d

      // data (split x and y values)
      const { [xAxis]: x = undefined, ...y } = record
      const payload: ChartOverlayData<T> = {
        index: Math.abs(Math.round(index)),
        d,
        x: typeof x === 'number' ? x : undefined,
        y: y as T, // see comment in ChartOverlayData
      }

      // hovered
      if (store) {
        store.data.onHover({ y })
      }

      // events
      emit('hover', payload)

      // render
      if (defaultSlot) {
        // render
        // the slot types x as a number; it is undefined when the x-axis key is missing from the data
        renderTooltip(instance, tooltip, () => defaultSlot(payload as Omit<ChartOverlayData<T>, 'x'> & { x: number }))
      } else {
        tooltip.innerHTML = `${xLabel} ${x}`
      }
      prevTemplate = container.innerHTML
    }

    // otherwise return cached template
    return prevTemplate
  }

  // return
  return {
    duration: 0,
    color: color || [...(store?.attr.colors || [])].reverse(),
    // snapToData: false,
    // getCircles(x: number | Date, data: DataRecord[], yScale) {
    //    return data[3]
    // },
    ...rest,
    template,
  }
})

const tooltipOptions = computed(() => ({ attributes: tooltipAttributes(attrs.class) }))
</script>

<script lang="ts">
export interface ChartOverlayProps {
  xAxis?: string
  meta?: ChartContext['meta']
  color?: string | string[]
}

/**
 * Data structure for overlay and hover events
 */
export interface ChartOverlayData<T> {
  // The index of the hovered data point
  index: number

  // The x-axis value (if x-axis prop or provided)
  // This will be undefined if xAxis key doesn't exist in the data
  x: number | undefined

  // The y-axis values (if x-axis prop or provided)
  // At runtime, this will exclude the xAxis property, but for type simplicity
  // (issues with props vs inject) we maintain the full type T
  y: T

  // The complete data object
  d: T
}

export interface ChartOverlayEmitProps<T> {
  (e: 'hover', data: ChartOverlayData<T>): void
}
</script>

<template>
  <VisCrosshair v-bind="crosshairOptions" />
  <VisTooltip v-bind="tooltipOptions" />
</template>

<style>
/* required as adding classes to tooltip disables the transition */
.unovis-xy-container {
  div[data-name='tooltip'] {
    z-index: var(--z-index-popover);
    pointer-events: none;
    transition: opacity 0.5s;
    opacity: 0;
  }

  &:hover div[data-name='tooltip'] {
    transition: opacity 0.5s;
    opacity: 0.9;
  }
}
</style>
